import { formatDate } from '@Core/lib/tasks/utils/date';
import { formatLocation } from '@Core/lib/tasks/utils/location';
import { formatMoney } from '@Core/lib/tasks/utils/money';
import { formatNumber } from '@Core/lib/tasks/utils/number';
import { formatTime } from '@Core/lib/tasks/utils/time';
import { warnSdkUtilFallback } from '@Core/lib/tasks/utils/warn';
import { isRecord } from '@Core/utils/recordGuards';
import type {
  ResourceRecord,
  SdkBadgeVariant,
  SdkDisplayFormatOptions,
  SdkDisplayUtils,
  SdkResourceField
} from '@Pages/pageRuntime/sdk/types';

import { markdownPlainText } from './markdown';
import { phoneUtils } from './phone';
import { tiptapPlainText } from './tiptap';

const DEFAULT_DATE_FORMAT = 'MMM d, yyyy';
const DEFAULT_TIME_FORMAT = 'h:mm a';
const DEFAULT_DATETIME_FORMAT = 'MMM d, yyyy h:mm a';

const unitFormatters = new Map<string, Intl.NumberFormat>();

// WebKit only exposes Intl.ListFormat when the OS ICU is new enough; Catalina Safari lacks it.
const shortUnitListFormatter =
  typeof Intl.ListFormat === 'function'
    ? new Intl.ListFormat(undefined, { style: 'short', type: 'unit' })
    : null;

function formatShortUnitList(labels: string[]): string {
  return shortUnitListFormatter === null
    ? labels.join(', ')
    : shortUnitListFormatter.format(labels);
}

function getUnitFormatter(unit: string): Intl.NumberFormat {
  if (!unitFormatters.has(unit)) {
    unitFormatters.set(
      unit,
      new Intl.NumberFormat(undefined, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 1
      })
    );
  }

  return unitFormatters.get(unit)!;
}

type SelectOption = {
  value?: unknown;
  label?: unknown;
  color?: unknown;
};

function configuredOptions(field: SdkResourceField): SelectOption[] {
  const options = field.settings?.options ?? field.settings?.ui?.options;

  return Array.isArray(options) ? options.filter(isRecord) : [];
}

function selectOption(field: SdkResourceField, value: unknown): SelectOption | undefined {
  return configuredOptions(field).find((option) => String(option.value) === String(value));
}

function displayType(field: SdkResourceField): string {
  const configured = field.settings?.ui?.display_type;
  return typeof configured === 'string' ? configured : field.type;
}

function formatTimeValue(field: SdkResourceField, value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) {
    warnSdkUtilFallback('time.format', 'The time is invalid; returning null.');
    return null;
  }

  const time = value.length === 5 ? `${value}:00` : value;
  const format = field.settings?.ui?.display_format ?? DEFAULT_TIME_FORMAT;
  return formatTime(`1970-01-01T${time}Z`, format, 'UTC');
}

function formatDurationValue(value: unknown, options: SdkDisplayFormatOptions): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error('The duration is not a finite number.');
  }

  const multipliers = {
    milliseconds: 0.001,
    seconds: 1,
    minutes: 60,
    hours: 3600
  } as const;
  const unit = options.durationUnit;
  if (typeof unit !== 'string' || !(unit in multipliers)) {
    throw new Error('The duration unit is invalid.');
  }

  const seconds = value * multipliers[unit as keyof typeof multipliers];
  const sign = seconds < 0 ? -1 : 1;
  let remaining = Math.abs(seconds);
  const values: Array<{ value: number; unit: 'day' | 'hour' | 'minute' | 'second' }> = [];

  for (const [unit, size] of [
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60]
  ] as const) {
    const amount = Math.floor(remaining / size);
    if (amount > 0) {
      values.push({ value: amount, unit });
      remaining -= amount * size;
    }
  }

  if (values.length === 0 || (remaining > 0 && values.length < 2)) {
    values.push({ value: remaining, unit: 'second' });
  }

  const labels = values
    .slice(0, 2)
    .map(({ value: amount, unit }, index) =>
      getUnitFormatter(unit).format(amount * (index === 0 ? sign : 1))
    );

  return formatShortUnitList(labels);
}

function formatSelectValue(field: SdkResourceField, value: unknown): string {
  const option = selectOption(field, value);
  const label = option?.label;

  return typeof label === 'string' && label !== '' ? label : String(option?.value ?? value);
}

function formatFieldValue(
  field: SdkResourceField | null | undefined,
  value: unknown,
  options: SdkDisplayFormatOptions | undefined
): string | null {
  if (!field || !isRecord(field) || typeof field.type !== 'string') {
    warnSdkUtilFallback('display.format', 'The field is malformed; returning null.');
    return null;
  }

  if (value === null || value === undefined || value === '') {
    warnSdkUtilFallback('display.format', 'No value was provided; returning null.');
    return null;
  }

  try {
    if (options?.durationUnit) {
      return formatDurationValue(value, options);
    }

    switch (displayType(field)) {
      case 'date':
        return formatDate(
          typeof value === 'string' ? value : null,
          field.settings?.ui?.display_format ?? DEFAULT_DATE_FORMAT
        );
      case 'time':
        return formatTimeValue(field, value);
      case 'datetime':
        return formatDate(
          typeof value === 'string' ? value : null,
          field.settings?.ui?.display_format ?? DEFAULT_DATETIME_FORMAT
        );
      case 'money':
        return formatMoney(value as Parameters<typeof formatMoney>[0]);
      case 'phone':
        return phoneUtils.format(value, { format: options?.phoneFormat ?? 'national' });
      case 'location':
        return formatLocation(value);
      case 'select':
        return formatSelectValue(field, value);
      case 'number':
        return formatNumber(value);
      case 'content':
        return tiptapPlainText(value) || null;
      case 'markdown':
        if (typeof value !== 'string') {
          throw new Error('The markdown value is not a string.');
        }
        return markdownPlainText(value) || null;
      case 'boolean':
        if (typeof value !== 'boolean') {
          throw new Error('The value is not a boolean.');
        }
        return value ? (field.settings?.yes_label ?? 'Yes') : (field.settings?.no_label ?? 'No');
      default:
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return String(value);
        }
        throw new Error(`The ${field.type} field value is not displayable as text.`);
    }
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    warnSdkUtilFallback(
      'display.format',
      `The value for ${field.key || 'the field'} could not be formatted: ${detail} Returning null.`
    );
    return null;
  }
}

function initials(name: string | null | undefined, count: number = 2): string {
  if (typeof name !== 'string' || name.trim() === '') {
    warnSdkUtilFallback('display.initials', 'No name was provided; returning an empty string.');
    return '';
  }

  if (!Number.isInteger(count) || count < 1) {
    warnSdkUtilFallback(
      'display.initials',
      'The count must be a positive integer; returning an empty string.'
    );
    return '';
  }

  return name
    .trim()
    .split(/\s+/)
    .slice(0, count)
    .map((part) => Array.from(part)[0] ?? '')
    .join('')
    .toLocaleUpperCase();
}

function badgeVariant(field: SdkResourceField | null | undefined, value: unknown): SdkBadgeVariant {
  if (!field || !isRecord(field)) {
    warnSdkUtilFallback(
      'display.badgeVariant',
      'The field is malformed; returning the secondary variant.'
    );
    return 'secondary';
  }

  const color = selectOption(field, value)?.color;
  if (typeof color !== 'string') {
    return 'secondary';
  }

  switch (color.trim().toLowerCase()) {
    case 'green':
    case 'emerald':
      return 'success';
    case 'yellow':
    case 'orange':
      return 'warning';
    case 'red':
      return 'destructive';
    case 'blue':
    case 'pink':
    case 'purple':
      return 'default';
    default:
      return 'secondary';
  }
}

function fieldValue(
  record: ResourceRecord | { fields?: ResourceRecord | null } | null | undefined,
  field: string | { readonly key: string }
): unknown | null {
  const key = typeof field === 'string' ? field : field?.key;

  if (typeof key !== 'string' || key.trim() === '') {
    warnSdkUtilFallback('display.fieldValue', 'The field selector is malformed; returning null.');
    return null;
  }

  if (record === null || record === undefined) {
    return null;
  }

  if (!isRecord(record)) {
    warnSdkUtilFallback('display.fieldValue', 'The record is malformed; returning null.');
    return null;
  }

  const nested = isRecord(record.fields) ? record.fields : null;
  if (nested && Object.hasOwn(nested, key)) {
    return nested[key] ?? null;
  }

  return Object.hasOwn(record, key) ? ((record as ResourceRecord)[key] ?? null) : null;
}

export function createDisplayUtils(): SdkDisplayUtils {
  return {
    format: formatFieldValue,
    initials,
    badgeVariant,
    fieldValue: fieldValue as SdkDisplayUtils['fieldValue']
  };
}
